import Papa, { ParseResult } from 'papaparse';
import * as XLSX from 'xlsx';
import { FacilityType } from 'src/routes/Customers/components/OperatorsTab/modules/types';

export const tenantsFromFile = (
  file: File,
  organizationId: string,
  emrType: string,
): Promise<FacilityType[]> => {
  return new Promise((resolve, reject) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (fileExtension === 'csv') {
      // Parse CSV
      Papa.parse<string[]>(file, {
        complete: (result: ParseResult<string[]>) => {
          try {
            const seenFacilityIds = new Set<string>();
            const parsedData: FacilityType[] = result.data
              .slice(1) // Skip header row
              .filter((row: string[]) => row[0] && row[1])
              .reduce<FacilityType[]>((acc, row: string[]) => {
                const facilityId = row[0];
                const facilityName = row[1];
                if (!facilityId || !facilityName) {
                  throw new Error(
                    `Invalid row data: facilityId=${row[0] ?? 'undefined'}, facilityName=${row[1] ?? 'undefined'}`,
                  );
                }
                if (seenFacilityIds.has(facilityId)) {
                  return acc; // Skip duplicates, no null to filter
                }
                seenFacilityIds.add(facilityId);
                acc.push({
                  id: facilityId,
                  facilityName: facilityName,
                  organizationId: organizationId,
                  emrType: emrType,
                });
                return acc;
              }, []);
            resolve(parsedData);
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            reject(new Error(`CSV parsing error: ${errorMessage}`));
          }
        },
        error: error => {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          reject(new Error(`CSV reading error: ${errorMessage}`));
        },
        header: false, // Set to true if CSV has headers, adjust mapping
        skipEmptyLines: true,
      });
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      // Parse Excel
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const seenFacilityIds = new Set<string>();
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          // Check if SheetNames is empty
          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            throw new Error('No sheets found in the Excel file');
          }
          const sheetName = workbook.SheetNames[0];
          // Check if sheet exists
          if (!sheetName || !workbook.Sheets[sheetName]) {
            throw new Error(
              `Sheet "${sheetName ?? 'undefined'}" not found in the workbook`,
            );
          }
          // Type assertion after safety check
          const worksheet: XLSX.WorkSheet = workbook.Sheets[
            sheetName
          ] as XLSX.WorkSheet;
          // Type jsonData as an array of arrays of strings
          const jsonData: string[][] = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
          });

          // Transform jsonData into FacilityType array
          const parsedData: FacilityType[] = jsonData
            .slice(1) // Skip header row
            .filter((row: string[]) => {
              return (
                row &&
                Array.isArray(row) &&
                row[0] !== undefined &&
                row[1] !== undefined
              );
            }) // Ensure facilityId and facilityName exist
            .reduce<FacilityType[]>((acc, row: string[]) => {
              const facilityId = row[0];
              const facilityName = row[1];
              if (!facilityId || !facilityName) {
                throw new Error(
                  `Invalid row data: facilityId=${row[0] ?? 'undefined'}, facilityName=${row[1] ?? 'undefined'}`,
                );
              }
              if (seenFacilityIds.has(facilityId)) {
                return acc; // Skip duplicates
              }
              seenFacilityIds.add(facilityId);
              acc.push({
                id: facilityId,
                facilityName: facilityName,
                organizationId: organizationId,
                emrType: emrType,
              });
              return acc;
            }, []);
          resolve(parsedData);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          reject(new Error(`Excel parsing error: ${errorMessage}`));
        }
      };
      reader.onerror = () => reject(new Error('Error reading Excel file'));
      reader.readAsArrayBuffer(file);
    } else {
      reject(
        new Error('Unsupported file type! Please upload a CSV or Excel file.'),
      );
    }
  });
};
